import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { spendTrendData } from "../../data/mock";
import { type ExecutiveTransaction } from "../../data/executiveOverview";

interface TooltipEntry {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid var(--border)",
      borderRadius: 8,
      padding: "10px 14px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 3 }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: p.color, display: "inline-block",
          }} />
          <span style={{ color: "var(--text-secondary)" }}>{p.name}:</span>
          <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>${p.value}M</span>
        </div>
      ))}
    </div>
  );
}

export function SpendTrendChart({ rows }: { rows?: ExecutiveTransaction[] }) {
  const years = new Set(rows?.map((row) => row.Year));
  const data = rows ? Array.from({ length: 36 }, (_, index) => {
    const year = 2024 + Math.floor(index / 12);
    const month = index % 12 + 1;
    const monthly = rows.filter((row) => row.Year === year && row.Month_No === month);
    return {
      year,
      month: `${new Date(year, month - 1).toLocaleString("en-US", { month: "short" })} ${String(year).slice(-2)}`,
      managed: monthly.reduce((sum, row) => sum + row.Spend_Amount_USD, 0) / 1_000_000,
      savings: monthly.reduce((sum, row) => sum + row.Savings_Identified_USD, 0) / 1_000_000,
      budget: monthly.reduce((sum, row) => sum + row.Budget_Amount_USD, 0) / 1_000_000,
    };
  }).filter((point) => !rows.length || years.has(point.year)) : spendTrendData;
  return (
    <div className="chart-panel">
      <div className="chart-panel__title">Spend Trend</div>
      <div className="chart-panel__sub">Monthly spend {rows ? "across the selected years" : "over last 12 months"} (USD millions)</div>
      <div style={{ height: 200, marginTop: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: "var(--text-muted)" }}
              tickLine={false}
              axisLine={false}
              interval={rows ? 2 : "preserveEnd"}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "var(--text-muted)" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `$${v}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, paddingTop: 8, color: "var(--text-secondary)" }}
            />
            {/* No custom <defs> children — use fillOpacity instead to avoid recharts duplicate-key bug */}
            <Area
              type="monotone"
              dataKey="managed"
              name="Spend"
              stroke="#6b3fa0"
              strokeWidth={2}
              fill="#6b3fa0"
              fillOpacity={0.10}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
            <Area
              type="monotone"
              dataKey="savings"
              name="Savings"
              stroke="#e0044e"
              strokeWidth={2}
              fill="#e0044e"
              fillOpacity={0.08}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
            <Area
              type="monotone"
              dataKey="budget"
              name="Budget"
              stroke="#3265aa"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              fill="#3265aa"
              fillOpacity={0.05}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
